import React, { useState, useEffect } from 'react';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Crown,
  HelpCircle,
  RotateCcw,
  ExternalLink
} from 'lucide-react';
import { UserProfile } from '../types';
import { saveUserProfile } from '../lib/firebase';

interface PaymentConfirmationProps {
  userProfile: UserProfile;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile>>;
  onNavigateToDashboard: () => void;
  openPricingModal: () => void;
}

export const PaymentConfirmation: React.FC<PaymentConfirmationProps> = ({
  userProfile,
  setUserProfile,
  onNavigateToDashboard,
  openPricingModal
}) => {
  const [status, setStatus] = useState<'loading' | 'success' | 'failed' | 'no_ref'>('loading');
  const [reference, setReference] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [verifiedProfile, setVerifiedProfile] = useState<UserProfile | null>(null);
  const [countdown, setCountdown] = useState<number>(5);

  useEffect(() => {
    // 1. Extract reference from URL (check both hash query and search query)
    let ref: string | null = null;

    // Check search params
    const searchParams = new URLSearchParams(window.location.search);
    ref = searchParams.get('reference') || searchParams.get('trxref');

    // Check hash query params (e.g. #/payment/confirm?reference=XYZ or #/payment/confirm?trxref=XYZ)
    if (!ref && window.location.hash.includes('?')) {
      const hashQuery = window.location.hash.substring(window.location.hash.indexOf('?'));
      const hashParams = new URLSearchParams(hashQuery);
      ref = hashParams.get('reference') || hashParams.get('trxref');
    }

    if (!ref) {
      // If no reference in URL, check if current user already has active subscription
      if (userProfile && userProfile.tier !== 'free') {
        setStatus('success');
        setVerifiedProfile(userProfile);
      } else {
        setStatus('no_ref');
      }
      return;
    }

    setReference(ref);

    // 2. Call backend server-side verification endpoint with polling resilience for 3DS / Bank Auth
    async function verifyPayment(refCode: string, attempt: number = 1) {
      if (attempt === 1) {
        setStatus('loading');
        setErrorMessage(null);
      }

      try {
        console.log(`[PaymentConfirmation] Calling backend verification for reference: ${refCode} (attempt ${attempt})`);
        const response = await fetch(`/api/paystack/verify?reference=${encodeURIComponent(refCode)}`);
        const data = await response.json();

        if (response.ok && data?.success && data?.profile) {
          console.log('[PaymentConfirmation] Server successfully verified transaction:', data.profile);
          setVerifiedProfile(data.profile);
          setUserProfile(prev => ({
            ...prev,
            ...data.profile
          }));

          // Synchronize profile client-side
          try {
            await saveUserProfile(data.profile);
          } catch (pErr) {
            console.warn('[PaymentConfirmation] Client profile sync warning:', pErr);
          }

          setStatus('success');
          return;
        }

        // Check if transaction is still pending / undergoing bank auth (status === 'ongoing' or 'pending')
        const rawStatus = data?.details?.status || data?.status;
        if (rawStatus === 'ongoing' || rawStatus === 'pending' || rawStatus === 'processing') {
          if (attempt < 8) {
            console.log(`[PaymentConfirmation] Transaction status is '${rawStatus}'. Waiting for 3DS/Bank auth resolution (attempt ${attempt}/8)...`);
            setTimeout(() => {
              verifyPayment(refCode, attempt + 1);
            }, 2500);
            return;
          }
        }

        // Final failure or declined state
        console.warn('[PaymentConfirmation] Verification failed or rejected:', data);
        const declineReason = data?.details?.gateway_response || data?.message || data?.error || 'Payment was declined or not completed.';
        setErrorMessage(declineReason);
        setStatus('failed');
      } catch (err: any) {
        if (attempt < 4) {
          console.warn(`[PaymentConfirmation] Network glitch on attempt ${attempt}, retrying in 2s...`);
          setTimeout(() => {
            verifyPayment(refCode, attempt + 1);
          }, 2000);
          return;
        }
        console.error('[PaymentConfirmation] Error verifying transaction with server:', err);
        setErrorMessage(err?.message || 'Could not connect to payment verification server.');
        setStatus('failed');
      }
    }

    verifyPayment(ref);
  }, []);

  // Countdown timer on verified success
  useEffect(() => {
    if (status !== 'success') return;

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          onNavigateToDashboard();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [status, onNavigateToDashboard]);

  const activeTierLabel = (() => {
    const tier = verifiedProfile?.tier || userProfile?.tier || 'standard';
    switch (tier) {
      case 'basic': return 'Basic Access (1 Month)';
      case 'standard': return 'Standard Access (2 Months)';
      case 'extended': return 'Extended Access (4 Months)';
      case 'unlimited':
      case 'all_access': return 'All-Access Pass (12 Months)';
      default: return 'Subscriber Access';
    }
  })();

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden text-center">

        {/* Top Accent Strip */}
        <div className={`h-2.5 w-full ${
          status === 'loading' ? 'bg-indigo-600 animate-pulse' :
          status === 'success' ? 'bg-emerald-500' :
          status === 'no_ref' ? 'bg-slate-400' : 'bg-rose-500'
        }`} />

        <div className="p-6 sm:p-8 space-y-6">

          {/* 1. LOADING STATE */}
          {status === 'loading' && (
            <div className="space-y-4 py-6">
              <div className="w-16 h-16 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center mx-auto ring-8 ring-indigo-50/50">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                  Confirming Your Payment...
                </h2>
                <p className="text-sm text-slate-600 max-w-sm mx-auto">
                  Please hold on for a moment while we securely verify your Paystack transaction and activate your prep access.
                </p>
                {reference && (
                  <p className="text-xs text-slate-400 font-mono pt-2">
                    Ref: {reference}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* 2. SUCCESS STATE */}
          {status === 'success' && (
            <div className="space-y-5 py-2">
              <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto ring-8 ring-emerald-50/50">
                <CheckCircle2 className="w-9 h-9 text-emerald-600" />
              </div>

              <div className="space-y-2">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                  <Crown className="w-3.5 h-3.5 text-emerald-700" /> Payment Confirmed & Unlocked
                </div>
                <h2 className="text-2xl font-black text-slate-900 tracking-tight">
                  Welcome to Full Access!
                </h2>
                <p className="text-sm text-slate-600">
                  Your <strong className="text-slate-900 font-semibold">{activeTierLabel}</strong> is now active. All exam simulators, mock exam series, study guides, and AI scoring tools are unlocked.
                </p>
              </div>

              {/* Transaction Summary Card */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-left text-xs space-y-2">
                <div className="flex justify-between items-center text-slate-600">
                  <span>Subscription Plan:</span>
                  <span className="font-bold text-slate-900">{activeTierLabel}</span>
                </div>
                <div className="flex justify-between items-center text-slate-600">
                  <span>Account Email:</span>
                  <span className="font-semibold text-slate-900 truncate max-w-[200px]">{userProfile?.email || verifiedProfile?.email}</span>
                </div>
                {reference && (
                  <div className="flex justify-between items-center text-slate-600">
                    <span>Payment Reference:</span>
                    <span className="font-mono text-slate-700 text-[11px]">{reference}</span>
                  </div>
                )}
                <div className="flex justify-between items-center text-slate-600">
                  <span>Status:</span>
                  <span className="font-bold text-emerald-600 flex items-center gap-1">
                    <ShieldCheck className="w-3.5 h-3.5" /> Active & Verified
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 pt-2">
                <button
                  id="btn-goto-dashboard-success"
                  onClick={onNavigateToDashboard}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm py-3 px-6 rounded-xl transition shadow-md flex items-center justify-center gap-2 cursor-pointer"
                >
                  <span>Start Practicing Now</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
                <p className="text-xs text-slate-400">
                  Auto-redirecting in <span className="font-bold text-slate-700">{countdown}s</span>...
                </p>
              </div>
            </div>
          )}

          {/* 3. FAILED / ERROR STATE */}
          {status === 'failed' && (
            <div className="space-y-5 py-2">
              <div className="w-16 h-16 bg-rose-50 text-rose-600 rounded-full flex items-center justify-center mx-auto ring-8 ring-rose-50/50">
                <XCircle className="w-9 h-9 text-rose-600" />
              </div>

              <div className="space-y-2">
                <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
                  Payment Verification Incomplete
                </h2>
                <p className="text-sm text-slate-600">
                  {errorMessage || 'We could not confirm this transaction with Paystack. If you were charged, your access will be granted automatically via webhook momentarily.'}
                </p>
                {reference && (
                  <div className="inline-block bg-slate-100 px-3 py-1 rounded text-xs font-mono text-slate-600 mt-2">
                    Reference: {reference}
                  </div>
                )}
              </div>

              {/* Troubleshooting Options */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <button
                  id="btn-retry-payment-verification"
                  onClick={() => {
                    if (reference) {
                      const searchParams = new URLSearchParams(window.location.search);
                      window.location.reload();
                    } else {
                      openPricingModal();
                    }
                  }}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs py-2.5 px-4 rounded-xl transition flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Retry Verification</span>
                </button>

                <button
                  id="btn-open-plans-from-failed"
                  onClick={openPricingModal}
                  className="w-full bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold text-xs py-2.5 px-4 rounded-xl transition flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                  <span>View Plans</span>
                </button>
              </div>

              <div className="pt-2 border-t border-slate-100 flex items-center justify-center gap-4 text-xs text-slate-500">
                <a
                  href="https://wa.me/16615310357"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline inline-flex items-center gap-1 font-medium"
                >
                  <HelpCircle className="w-3.5 h-3.5" /> Contact Support via WhatsApp
                </a>
              </div>
            </div>
          )}

          {/* 4. NO REFERENCE SUPPLIED */}
          {status === 'no_ref' && (
            <div className="space-y-4 py-4">
              <div className="w-14 h-14 bg-slate-100 text-slate-600 rounded-full flex items-center justify-center mx-auto">
                <HelpCircle className="w-7 h-7 text-slate-500" />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-bold text-slate-900">
                  Payment Status Check
                </h2>
                <p className="text-sm text-slate-600">
                  No payment transaction reference was found in the URL. If you would like to unlock full access to all state exams and mock series, please select a plan.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <button
                  onClick={openPricingModal}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs py-2.5 px-4 rounded-xl transition cursor-pointer"
                >
                  View Subscription Plans
                </button>
                <button
                  onClick={onNavigateToDashboard}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs py-2.5 px-4 rounded-xl transition cursor-pointer"
                >
                  Return to Dashboard
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
